import type { Task } from "@app/core";
import { cn } from "@app/ui";
import { memo, useDeferredValue, useEffect, useMemo, useState, useRef, type CSSProperties } from "react";
import { AnimatePresence } from "../../lib/motion/index.js";
import { isDemoBoot } from "../../lib/demo-mode.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useI18n } from "../../lib/useI18n.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { useSidebarAnimStore, useTaskDialogStore } from "../../stores/index.js";
import { CalendarBackgroundMenu } from "./CalendarBackgroundMenu.js";
import { CurrentTimeBar } from "./CurrentTimeBar.js";
import { TaskBlock } from "./TaskBlock.js";
import { computeNestLayout, type NestInput } from "./nest-layout.js";
import { blockDragState, getDragTaskId, type BlockDragVisual } from "./drag.js";
import { useNestHoverStore } from "../../stores/index.js";
import type { CreatePreview } from "./useCreateDrag.js";

/** Default px-per-hour; the live value is persisted (Shift+scroll to zoom). */
export const HOUR_HEIGHT = 48;
const SNAP_MIN = 15;

export interface DayColumnProps {
  day: Date;
  tasks: Task[];
  overflowTasks: Array<{ task: Task; overflowMinutes: number }>;
  isToday: boolean;
  /** Last column in the grid (drops its right border). */
  isLast: boolean;
  /** px per hour (vertical zoom, persisted). */
  hourHeight: number;
  /** Highlight segment for an in-progress drag-to-create (grid-level). */
  createPreview: CreatePreview | null;
  /** Wash for the drag-to-create preview block (a new event in the default
   *  calendar). Computed ONCE in TimeAxisGrid (stable ref) and shared by every
   *  column so the columns don't each subscribe to calendars/settings/blockStyle. */
  createWash: { vars: CSSProperties; washed: boolean };
  onDrop: (minutesFromMidnight: number, taskId: string) => void;
  onTaskClick: (t: Task) => void;
  onTaskResize: (t: Task, newDurationMinutes: number) => void;
  onTaskResizeTop: (t: Task, newStartIso: string, newDurationMinutes: number) => void;
  /** Maps a cursor point to the grid instant under it, so a block's bottom edge
   *  can be dragged across day columns (cross-day resize). Grid-provided. */
  pointToInstant?: ((clientX: number, clientY: number) => Date | null) | undefined;
  /** This column's px width, measured ONCE by the grid (see `colWidthPx` there).
   *  0 until the grid has measured, which is when the column falls back to
   *  measuring itself. */
  colWidthPx: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
function bgGradient(h: number): string {
  // Demo iframes are CSS-scaled down by the host; a 1px line lands on
  // fractional device pixels there and intermittently vanishes — draw 2px.
  const w = isDemoBoot() ? 2 : 1;
  return `repeating-linear-gradient(to bottom, transparent 0, transparent ${h - w}px, hsl(var(--border)) ${h - w}px, hsl(var(--border)) ${h}px)`;
}

function minuteOfDay(task: Task): number {
  if (!task.due_at) return 0;
  const d = new Date(task.due_at);
  return d.getHours() * 60 + d.getMinutes();
}

function buildInputs(
  tasks: Task[],
  overflowTasks: Array<{ task: Task; overflowMinutes: number }>,
): NestInput[] {
  return [
    ...tasks.filter((t) => t.due_at).map((t) => ({
      task: t,
      startMin: minuteOfDay(t),
      duration: Math.max(t.duration_minutes, 15),
      isOverflow: false,
    })),
    ...overflowTasks.map(({ task, overflowMinutes }) => ({
      task,
      startMin: minuteOfDay(task),
      duration: Math.max(task.duration_minutes, 15),
      isOverflow: true,
      overflowMinutes: Math.min(overflowMinutes, 24 * 60),
    })),
  ];
}

const blockKeyOf = (entry: { task: Task; isOverflow?: boolean }): string =>
  entry.isOverflow ? `overflow-${entry.task.id}` : entry.task.id;

/** Removals in one commit past which a column's exits are treated as bulk. */
const BULK_EXIT_MIN = 4;

/**
 * How many blocks this commit is removing, as a boolean: "a few" or "a lot".
 *
 * A block's exit fade is per block and keeps it in the DOM (and in the
 * compositor) for the whole duration. One leaving block is a moved/deleted
 * entry and should linger; a whole column emptying is a FILTER, and 150+ blocks
 * fading in step is most of that interaction's blocked main thread — measured at
 * ~390 of `sidebar-topic-filter`'s ~440 ms. `TaskBlock` therefore picks its exit
 * duration from this flag, delivered through `AnimatePresence`'s `custom` (an
 * exiting child no longer receives props, so it cannot be told any other way).
 *
 * Derived in a `useMemo` on the layout's identity, not during every render: the
 * flag must describe the commit that removed the blocks and then hold still
 * while their exit runs, and `layout` is exactly the thing that changes when
 * blocks come and go.
 */
function useBulkExit(layout: Array<{ task: Task; isOverflow?: boolean }>): boolean {
  const prevKeys = useRef<Set<string>>(new Set());
  return useMemo(() => {
    const next = new Set(layout.map(blockKeyOf));
    let removed = 0;
    for (const key of prevKeys.current) if (!next.has(key)) removed++;
    prevKeys.current = next;
    return removed >= BULK_EXIT_MIN;
  }, [layout]);
}

function DayColumnImpl({ day: _day, tasks, overflowTasks, isToday, isLast, hourHeight, createPreview, createWash, onDrop, onTaskClick, onTaskResize, onTaskResizeTop, pointToInstant, colWidthPx }: DayColumnProps) {
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const tf = useTimeFormat();
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement | null>(null);
  const [hoverY, setHoverY] = useState<number | null>(null);
  // Live ghost of a grabbed block (offset-preserving), tracking the cursor.
  const [ghost, setGhost] = useState<{ topMin: number; durationMin: number; visual: BlockDragVisual } | null>(null);
  const [bgCtx, setBgCtx] = useState<{ x: number; y: number; min: number } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // The grid measures the column width ONCE for the whole track (all columns are
  // the same %-width of it), so the common case needs no per-column measurement
  // at all: by the time tasks hydrate the grid has long since measured, and this
  // column's FIRST block-bearing render already has the true width. Self-measure
  // only when the grid has nothing yet — a client-side nav into the route mounts
  // the grid with tasks already in the store, so its first render precedes its
  // own measurement.
  //
  // This used to be a per-column `useState(220)` placeholder plus a
  // `useDeferredValue`, which cost every column TWO block renders per mount (the
  // placeholder, then the real width) — ~500 wasted block renders on a dense
  // week boot.
  const [selfWidth, setSelfWidth] = useState(0);
  // The grid's value is already burst-coalesced, so only the self-measured
  // fallback needs its own — and, like the grid's, it must not defer the first
  // real width or the blocks mount at the placeholder and re-render.
  const selfDeferred = useDeferredValue(selfWidth);
  const measuredWidth = colWidthPx > 0 ? colWidthPx : selfDeferred > 0 ? selfDeferred : selfWidth;
  const colWidth = measuredWidth > 0 ? measuredWidth : 220;

  useEffect(() => {
    if (colWidthPx > 0 || !ref.current) return;
    const el = ref.current;
    const update = () => {
      // Skip while a sidebar gesture animates the viewport width per frame:
      // the %-sized column scales natively and the blocks are left/right-
      // anchored so they stretch with it — only the px nest-layout numbers go
      // briefly stale. One settle re-measure lands below when the gesture ends.
      if (useSidebarAnimStore.getState().active) return;
      setSelfWidth(el.clientWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [colWidthPx]);

  // Clear the hover line on any drop/dragend. A drop handled by a child block
  // (which stops propagation, e.g. nesting) never reaches this column's onDrop,
  // and when nesting unmounts the dragged node `dragend` can fail to fire — so we
  // listen for `drop` in the CAPTURE phase (runs before stopPropagation, and
  // before the node unmounts) plus `dragend` as a fallback.
  useEffect(() => {
    // Capture-phase `drop` clears ONLY the line (it runs before the target's
    // onDrop reads `armedId`, so it must NOT disarm — that broke dwell-insert).
    const clearLine = () => { setHoverY(null); setGhost(null); };
    const clearAll = () => { setHoverY(null); setGhost(null); useNestHoverStore.getState().disarm(); };
    window.addEventListener("drop", clearLine, true);
    window.addEventListener("dragend", clearAll);
    return () => { window.removeEventListener("drop", clearLine, true); window.removeEventListener("dragend", clearAll); };
  }, []);

  function handleExpand(id: string, active: boolean) {
    setExpandedId((cur) => (active ? id : cur === id ? null : cur));
  }

  function rawMinutes(clientY: number): number {
    if (!ref.current) return 0;
    const rect = ref.current.getBoundingClientRect();
    return ((clientY - rect.top) / hourHeight) * 60;
  }

  function snap(min: number): number {
    return Math.max(0, Math.min(24 * 60 - SNAP_MIN, Math.round(min / SNAP_MIN) * SNAP_MIN));
  }

  function computeMinutes(clientY: number): number {
    return Math.max(0, Math.min(24 * 60 - SNAP_MIN, Math.floor(rawMinutes(clientY) / SNAP_MIN) * SNAP_MIN));
  }

  /** Start minute for a drop, keeping the grabbed point under the cursor (the
   *  block's grab offset is subtracted) and snapping. Falls back to the plain
   *  cursor minute for external drags (inbox items have no grab offset). */
  function dropStartMin(clientY: number): number {
    const bd = blockDragState();
    return snap(rawMinutes(clientY) - (bd ? bd.grabOffsetMin : 0));
  }

  // Memoized so every `NestEntry` (including its `cover` object) keeps its
  // reference across column re-renders that don't change geometry — `TaskBlock`
  // is memoized on exactly these props, so a rebuilt layout array would
  // re-render every block in the column for a hover line or a width tick.
  const layout = useMemo(
    () => computeNestLayout(buildInputs(tasks, overflowTasks), hourHeight, colWidth),
    [tasks, overflowTasks, hourHeight, colWidth],
  );

  const bulkExit = useBulkExit(layout);

  // Previews render with the SAME geometry/look as real blocks: full-ish width
  // with the standard ~5% right gap, washed in real block colors. The wash itself
  // (default-calendar event color) is column-independent — computed once in
  // TimeAxisGrid and passed in — so the columns don't subscribe to the calendar/
  // settings/block-style stores N times.
  const previewRightPx = Math.max(2, Math.round(colWidth * 0.05));

  return (
    <div
      ref={ref}
      className={`relative select-none ${isLast ? "" : "border-r"}`}
      style={{
        height: HOURS.length * hourHeight,
        backgroundImage: bgGradient(hourHeight),
        backgroundColor: isToday ? "hsl(var(--primary) / 0.04)" : undefined,
      }}
      onContextMenu={(e) => {
        if (suppressMobileContextMenu(e)) return;
        if ((e.target as HTMLElement).closest("[data-task-block]")) return;
        e.preventDefault();
        setBgCtx({ x: e.clientX, y: e.clientY, min: computeMinutes(e.clientY) });
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const bd = blockDragState();
        if (bd) { setGhost({ topMin: dropStartMin(e.clientY), durationMin: bd.durationMin, visual: bd.visual }); setHoverY(null); }
        else setHoverY((computeMinutes(e.clientY) / 60) * hourHeight);
      }}
      onDragLeave={() => { setHoverY(null); setGhost(null); }}
      onDrop={(e) => {
        e.preventDefault();
        setHoverY(null);
        setGhost(null);
        const id = getDragTaskId(e);
        if (id) onDrop(dropStartMin(e.clientY), id);
      }}
    >
      {createPreview && (
        <div
          className={cn(
            "pointer-events-none absolute z-20 overflow-hidden rounded border px-1.5 py-0.5 shadow-md",
            createWash.washed && "task-wash",
          )}
          style={{
            ...createWash.vars,
            top: (createPreview.startMin / 60) * hourHeight,
            height: Math.max(((createPreview.endMin - createPreview.startMin) / 60) * hourHeight, 12),
            left: 1,
            right: previewRightPx,
          }}
        >
          {createPreview.showLabel && (
            <>
              <div className="break-words text-xs font-medium leading-tight">{t("calendar.newEvent")}</div>
              <div className="break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">
                {tf.range(createPreview.startMin, createPreview.endMin)}
              </div>
            </>
          )}
        </div>
      )}
      {hoverY !== null && (
        <div className="pointer-events-none absolute left-0 right-0 z-20 h-0.5 bg-primary" style={{ top: hoverY }} />
      )}
      {ghost && (
        <div
          className={cn(
            "pointer-events-none absolute z-40 overflow-hidden rounded border px-1.5 py-0.5 shadow-lg",
            ghost.visual.washed ? "task-wash" : ghost.visual.toneClass,
            ghost.visual.task.virtual && "border-dashed",
          )}
          style={{
            ...ghost.visual.vars,
            top: (ghost.topMin / 60) * hourHeight,
            height: Math.max((ghost.durationMin / 60) * hourHeight, 12),
            left: 1,
            right: previewRightPx,
          }}
        >
          <div className="break-words text-xs font-medium leading-tight">{ghost.visual.task.title}</div>
          <div className="break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">
            {tf.range(ghost.topMin, ghost.topMin + ghost.durationMin)}
          </div>
        </div>
      )}
      {isToday && <CurrentTimeBar hourHeight={hourHeight} />}
      <AnimatePresence initial={false} custom={bulkExit}>
      {layout.map((entry) => {
        const key = blockKeyOf(entry);
        return (
          <TaskBlock
            key={key}
            task={entry.task}
            hourHeight={hourHeight}
            topMin={entry.topMin}
            heightMin={entry.heightMin}
            naturalStartMin={entry.naturalStartMin}
            leftPx={entry.leftPx}
            colWidthPx={colWidth}
            depth={entry.zIndex}
            cover={entry.cover}
            expanded={expandedId === key}
            onExpandChange={(active) => handleExpand(key, active)}
            onClick={onTaskClick}
            onResize={onTaskResize}
            onResizeTop={onTaskResizeTop}
            pointToInstant={pointToInstant}
            isContinuation={entry.isOverflow}
          />
        );
      })}
      </AnimatePresence>
      {bgCtx && (
        <CalendarBackgroundMenu
          x={bgCtx.x}
          y={bgCtx.y}
          onClose={() => setBgCtx(null)}
          onCreate={() => {
            const date = new Date(_day);
            date.setHours(Math.floor(bgCtx.min / 60), bgCtx.min % 60, 0, 0);
            openCreate(date);
          }}
        />
      )}
    </div>
  );
}

/**
 * Re-render only when a column's DATA changes. A day-shift keeps the window
 * stable (same dates, same memoized task arrays) so every column's data props
 * are referentially equal — the columns skip rendering and the whole track just
 * translates. Function props are intentionally ignored, and a column may now keep
 * STALE closures across an edit (unchanged days — incl. empty ones — preserve
 * their DayCol ref and skip rendering). That is only safe because the grid's
 * handlers (`onDrop`/resize via `useCalendarMutations`) read their task/rule data
 * LIVE from the store at call time rather than from a captured snapshot — never
 * reintroduce a closed-over `tasks`/`rules` into a handler reached from here.
 */
export const DayColumn = memo(DayColumnImpl, (a, b) =>
  a.day === b.day &&
  a.tasks === b.tasks &&
  a.overflowTasks === b.overflowTasks &&
  a.isToday === b.isToday &&
  a.isLast === b.isLast &&
  a.hourHeight === b.hourHeight &&
  a.createPreview === b.createPreview &&
  a.createWash === b.createWash,
);
